import * as React from 'react'
import { AlertOctagon, RotateCcw, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'

/**
 * Failure surfaces for the console.
 *
 * Three things share one presentation:
 *  - `ErrorBoundary` — a real class boundary, because render-phase throws are
 *    the only errors React will not let a hook catch.
 *  - `RouteErrorComponent` — handed to TanStack Router, which catches loader
 *    and render errors per route and gives us its own `reset`.
 *  - `NotFoundPanel` — a 404 is a failure the operator caused, so it reads
 *    calmer, but it lives here so the chrome cannot drift.
 *
 * The stack is dev-only on purpose: a production console is shoulder-surfed in
 * a warehouse office, and a component stack is noise to the person reading it
 * and detail to anyone standing behind them.
 */

/* ------------------------------------------------------------------ *
 * Error shape normalisation
 * ------------------------------------------------------------------ */

/** Anything can be thrown in JS. Get a printable line out of it regardless. */
function messageOf(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error !== '') return error
  if (error && typeof error === 'object') {
    const rec = error as Record<string, unknown>
    const candidate = rec['message'] ?? rec['error']
    if (typeof candidate === 'string' && candidate !== '') return candidate
  }
  return 'The view failed to render.'
}

/** `ApiError` and friends carry a status; surface it as a code chip. */
function statusOf(error: unknown): number | null {
  if (error && typeof error === 'object') {
    const status = (error as Record<string, unknown>)['status']
    if (typeof status === 'number' && Number.isFinite(status)) return status
  }
  return null
}

function stackOf(error: unknown): string | null {
  if (error instanceof Error && typeof error.stack === 'string' && error.stack !== '') {
    return error.stack
  }
  return null
}

/* ------------------------------------------------------------------ *
 * Shared panel
 * ------------------------------------------------------------------ */

export interface ErrorPanelProps {
  error: unknown
  /** React's component stack, when a boundary captured one. */
  componentStack?: string | null
  /** Retry without a full page load. Omitted when there is nothing to retry. */
  onRetry?: () => void
  title?: string
  className?: string
}

export function ErrorPanel({
  error,
  componentStack = null,
  onRetry,
  title = 'Something broke',
  className,
}: ErrorPanelProps) {
  const status = statusOf(error)
  const stack = stackOf(error)
  const showDetail = import.meta.env.DEV && (stack !== null || componentStack !== null)

  return (
    <div
      role="alert"
      className={cn('flex min-h-[280px] w-full items-start justify-center px-6 py-12', className)}
    >
      <div className="w-full max-w-[560px] space-y-4">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-px flex size-9 flex-none items-center justify-center rounded-lg border border-danger/30 bg-danger-soft text-danger [&_svg]:size-[18px]"
          >
            <AlertOctagon />
          </span>

          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-[15px] font-semibold text-fg">{title}</h2>
              {status !== null ? (
                <span className="rounded-sm border border-danger/30 bg-danger-soft px-1.5 py-px font-mono text-[11px] text-danger tabular">
                  {status}
                </span>
              ) : null}
            </div>
            <p className="text-[13px] leading-[1.55] break-words text-muted">{messageOf(error)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pl-12">
          {onRetry ? (
            <Button variant="primary" size="sm" onClick={onRetry}>
              <RotateCcw aria-hidden="true" />
              Try again
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              window.location.reload()
            }}
          >
            <Undo2 aria-hidden="true" />
            Reload console
          </Button>
        </div>

        {showDetail ? (
          <details className="ml-12 rounded-md border border-line bg-inset">
            <summary className="cursor-pointer list-none px-3 py-2 text-[12px] font-medium text-muted select-none hover:text-fg">
              Stack trace
              <span className="ml-1.5 text-faint">(dev only)</span>
            </summary>
            <div className="max-h-[320px] overflow-auto border-t border-line px-3 py-2">
              {stack !== null ? (
                <pre className="font-mono text-[11.5px] leading-[1.6] whitespace-pre-wrap text-faint">
                  {stack}
                </pre>
              ) : null}
              {componentStack !== null ? (
                <pre className="mt-2 font-mono text-[11.5px] leading-[1.6] whitespace-pre-wrap text-faint">
                  {componentStack.trim()}
                </pre>
              ) : null}
            </div>
          </details>
        ) : null}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Route-level error component (TanStack Router)
 * ------------------------------------------------------------------ */

export interface RouteErrorProps {
  error: unknown
  reset: () => void
  info?: { componentStack: string }
}

/**
 * Router hands us its own `reset`, which re-runs the failed match rather than
 * reloading the document — the difference between losing the operator's
 * filters and not.
 */
export function RouteErrorComponent({ error, reset, info }: RouteErrorProps) {
  return (
    <ErrorPanel
      error={error}
      componentStack={info?.componentStack ?? null}
      onRetry={reset}
      title="This view failed to load"
    />
  )
}

/* ------------------------------------------------------------------ *
 * Not found
 * ------------------------------------------------------------------ */

export function NotFoundPanel() {
  return (
    <div className="flex min-h-[280px] w-full items-center justify-center px-6 py-12">
      <div className="max-w-[420px] space-y-3 text-center">
        <p className="font-mono text-[11px] tracking-[0.14em] text-faint uppercase">404</p>
        <h2 className="font-display text-[15px] font-semibold text-fg">No such page</h2>
        <p className="text-[13px] leading-[1.55] text-muted">
          That URL does not match any view in the console. It may have been a link to an older
          version of the panel.
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Class boundary
 * ------------------------------------------------------------------ */

export interface ErrorBoundaryProps {
  children: React.ReactNode
  /**
   * When this value changes the boundary clears itself. The shell passes the
   * pathname, so navigating away from a broken view actually leaves it —
   * otherwise a caught error pins the fallback forever and the nav looks dead.
   */
  resetKey?: string | number
  /** Replaces the default panel; receives everything needed to recover. */
  fallback?: (ctx: {
    error: unknown
    componentStack: string | null
    reset: () => void
  }) => React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: unknown
  componentStack: string | null
}

const CLEAN: ErrorBoundaryState = { hasError: false, error: null, componentStack: null }

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = CLEAN

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  override componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null })
    // Deliberately console-only: this app has no error reporting sink, and a
    // silent catch is how a broken deploy stays broken.
    console.error('[console] render error', error, info.componentStack)
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState(CLEAN)
    }
  }

  private readonly reset = (): void => {
    this.setState(CLEAN)
  }

  override render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback({
        error: this.state.error,
        componentStack: this.state.componentStack,
        reset: this.reset,
      })
    }

    return (
      <ErrorPanel
        error={this.state.error}
        componentStack={this.state.componentStack}
        onRetry={this.reset}
      />
    )
  }
}
